<template>
  <section class="destination-account-section">
    <!-- Form itself -->
    <form @submit.prevent="" v-if="!shouldShowReview">
      <p v-if="isCorridorNotOpen" class="banner warning">
        This corridor is not available for your account. Please contact Support at <a href="mailto:support@triple-a.io?subject=Help opening new corridor">support@triple-a.io</a> to enable payouts to this country.
      </p>
      <fieldset v-else>
        <legend>Step 3 of 3: Payment Details</legend>
        <!-- Type hidden because there is only one value : bank_account -->
        <!-- <label v-if="form.type">
        Payment method
        <vs-select autocomplete v-model="form.type.val"
          :danger="hasBeenSubmitted && !form.type.val"
          danger-text="Please select a payment method">
          <vs-select-item selected :value="form.type.acceptedValues[0]" :text="form.type.acceptedValues[0].display" />
        </vs-select>
      </label> -->
        <!-- Bank selection -->
        <label
          v-if="!shouldBankBeHidden">Bank <span class="req">*</span>
          <vs-select v-model="form.bank.val"
            autocomplete
            id="bank-list-input"
            :disabled="!bankList || isBankListLoading "
            :placeholder="`${isBankListLoading ? 'Loading...' : 'Please select a bank'}`"
            :danger="errors.bank"
            danger-text="Please select a Bank"
            @change="handleBankSelect()">
            <vs-select-item v-for="item in bankList" :key="'bank_' + item.id" :value="item" :text="`${item.name}${item.currency ? ' (' + item.currency + ')' : ''} ${item.extra_data && item.extra_data.branch_code ? ' - ' + item.extra_data.branch_code : ''}`" />
          </vs-select>
          <div class="vs-con-loading__container" id="bank-list-loading" :disabled="!bankList || isBankListLoading"></div>
        </label>
        <!-- Branch code -->
        <label v-if="form.branch_code" class="small">
          Bank Branch code <span class="req">*</span>
          <input v-model.trim="form.branch_code.val" maxlength="50"
            :is-error="errors.branch_code"
            :required="form.branch_code" 
            @change="errors.branch_code = false"/>
        </label>
        <!-- Account number -->
        <label v-if="form.account_number">
          {{ dynamicLabels.account_number }} <span class="req">*</span>
          <input v-model.trim="form.account_number.val"
            :is-error="errors.account_number"
            @change="errors.account_number = false" />
          <span>Please input a valid Account number</span>
        </label>
        <!-- Account format -->
        <label class="small" v-if="form.account_type && form.account_type.acceptedValues.length > 1">
          Account format <span class="req">*</span>
          <vs-select v-model="form.account_type.val" autocomplete
            :danger="errors.account_type"
            danger-text="Please select an account format"
            @change="errors.account_type = false">
            <vs-select-item v-for="item in form.account_type.acceptedValues" :key="item.val" :value="item" :text="item.display" />
          </vs-select>
        </label>
        <!-- Account type -->
        <label class="small" v-if="form.bank_account_type">
          Account type <span class="req">*</span>
          <span class="tooltip">
            <img
              src="../assets/icons/help.svg"
              alt="more info on Account type" />
            <span class="tooltip-text">
              <strong>Account type</strong>
              <br>
              A checking account is primarily used for everyday transactions and payments, while a savings account is designed to hold money for future goals and earn interest over time.
            </span>
          </span>
          <vs-select v-model="form.bank_account_type.val" autocomplete
            :danger="errors.bank_account_type"
            danger-text="Please select an Account type"
            @change="errors.bank_account_type = false">
            <vs-select-item v-for="item of form.bank_account_type.acceptedValues" :key="`bank_account_type_${item.val}`" :value="item" :text="item.display" />
          </vs-select>
        </label>
        <!-- Routing type -->
        <label class="small" v-if="form.routing_type && form.routing_type.acceptedValues.length > 1">
          Routing type <span class="req">*</span>
          <vs-select v-model="form.routing_type.val" autocomplete
            :danger="errors.routing_type"
            danger-text="Please select a Routing type"
            @change="errors.routing_type = false">
            <vs-select-item v-for="item in form.routing_type.acceptedValues" :key="item.val" :value="item" :text="item.display" />
          </vs-select>
        </label>
        <!-- Routing code -->
        <label v-if="form.routing_code">
          {{ dynamicLabels.routing_code }}<span class="req">*</span>
          <span class="tooltip">
            <img
              src="../assets/icons/help.svg"
              alt="more info on bank code" />
            <span class="tooltip-text">
              <strong>Routing code</strong>
              <br>
              A Routing code is a unique identifier assigned to financial institutions to facilitate electronic transactions and financial communications.
            </span>
          </span>
          <input v-model.trim="form.routing_code.val"
            :is-error="errors.routing_code"
            @change="errors.routing_code = false"
            />
          <span>Please enter a valid Routing code</span>
        </label>
      </fieldset>
    </form>

    <!-- Review section -->
    <div v-else-if="shouldShowReview">
      <h3>Payment Details</h3>
      <dl v-if="form">
        <div>
          <dt>Payment Method</dt>
          <dd>{{ form.type.val.display }}</dd>
        </div>
        <div v-if="form.external_id" class="full-row">
          <dt>External ID</dt>
          <dd>{{ form.external_id.val }}</dd>
        </div>
        <div class="full-row" v-if="!shouldBankBeHidden">
          <dt>Name of Bank</dt>
          <dd>{{ form.bank.val.name }}</dd>
        </div>
        <div>
          <dt>Country of Bank</dt>
          <dd>{{ form.bank.val.country_code }}</dd>
        </div>
        <div v-if="form.branch_code && form.branch_code.val">
          <dt> Bank Branch code</dt>
          <dd>{{ form.branch_code.val }}</dd>
        </div>
        <div>
          <dt>Currency of Account</dt>
          <dd>{{ form.bank.val.currency }}</dd>
        </div>
        <div v-if="form.account_type && form.account_type.val">
          <dt>Account Format</dt>
          <dd>{{ form.account_type.val.display }}</dd>
        </div>
        <div v-if="form.account_number.val">
          <dt>Account Number</dt>
          <dd>{{ form.account_number.val }}</dd>
        </div>
        <div v-if="form.bank_account_type && form.bank_account_type.val">
          <dt>Account Type</dt>
          <dd>{{ form.bank_account_type.val.display }}</dd>
        </div>
        <div v-if="form.routing_type && form.routing_type.val">
          <dt>Routing Type</dt>
          <dd>{{ form.routing_type.val.display }}</dd>
        </div>
        <div v-if="form.routing_code && form.routing_code.val">
          <dt>Routing Code</dt>
          <dd>{{ form.routing_code.val }}</dd>
        </div>
      </dl>
    </div>
  </section>
</template>

<script>
import { debounce } from '../plugins/debounceHelper';
export default {
  name: 'DestinationAccountForm',
  props: {
    requiredFields: {
      default: () => [],
    },
    bankList: {
      type: Array,
      default: () => [],
    },
    isBankListLoading: {
      type: Boolean,
      default: false,
    },
    hasBeenSubmitted: {
      type: Boolean,
      default: false,
    },
    shouldShowReview: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {
        type: { val: '' },
        bank: {
          val: '',
        },
        account_number: { val: '' },
        account_type: { val: {
          val: '',
          display: '',
        } },
        routing_type: { val: 
          {
            val: '',
            display: '',
          }
         },
        routing_code: { val: '' },
      },
      dynamicLabels: {
        account_number: 'Account Number',
        routing_code: 'Routing Code',
      },
      errors: {
        bank: false,
        branch_code: false,
        account_number: false,
        account_type: false,
        routing_code: false,
        routing_type: false,
      },
    };
  },
  created() {
    this.parseRequiredFields();
  },
  mounted() {
    this.attachBankInputListener();
  },
  methods: {
    parseRequiredFields() {
      this.resetForm();
      for (const field of this.requiredFields) {
        const fieldName = field.field.split('.').pop();
        this.$set(this.form, fieldName, {});
        if (field.accepted_values) {
          this.$set(this.form[fieldName], 'acceptedValues', this.parseAcceptedValues(field));
          this.$set(this.form[fieldName], 'val', this.form[fieldName].acceptedValues[0]);
        } else {
          this.$set(this.form[fieldName], 'val', '');
        }
      }
      this.preselectBank();
      this.preselectRoutingCode();
      this.preselectAccountType();
    },
    parseAcceptedValues(field) {
      if (!field.accepted_values) {
        return [];
      }
      const result = [];
      for (const value of field.accepted_values) {
        result.push({
          val: value,
          display: value.replace(/_/g, ' '),
        });
      }
      return result;
    },
    handleBankSelect() {
      if (!this.form.bank.val) {
        return;
      }
      // on bank select change all the related fields receiving_institution_id, currency, country_code
      // We rely on the bank choice rather than the parent component for robustness
      this.form.receiving_institution_id = { val: this.form.bank.val.id };
      this.form.currency = { val: this.form.bank.val.currency };
      this.form.country_code = { val: this.form.bank.val.country_code };
    },
    handleSubmit() {
      if (!this.isFormValid()) {
        this.$emit('formNotValid');
        return;
      }
      const account = {};
      this.cleanAccNum();
      account.type = this.form.type.val;
      account.receiving_institution_id = this.form.receiving_institution_id.val;
      account.currency = this.form.bank.val.currency;
      account.country_code = this.form.bank.val.country_code;
      account.account = {
        account_number: this.form.account_number ? this.form.account_number.val : '',
        account_type: this.form.account_type ? this.form.account_type.val.val : '', // Selects have 1 more layer
        bank_account_type: this.form.bank_account_type ? this.form.bank_account_type.val.val : '',
        routing_type: this.form.routing_type ? this.form.routing_type.val.val : '',
        routing_code: this.form.routing_code ? this.form.routing_code.val : '',
        branch_code: this.form.branch_code ? this.form.branch_code.val : '',
      };
      account.bank_name = this.form.bank.val.name;
      // Emit the form data
      this.$emit('submitted', account);
    },
    isFormValid() {
      this.resetErrors();
      let isValid = true;
      // Check if all the required fields are filled
      for (const field of this.requiredFields) {
        const fieldName = field.field.split('.').pop();
        const value = this.form[fieldName].val.hasOwnProperty('val') ? this.form[fieldName].val.val : this.form[fieldName].val;
        // skipping account name since its computed by the parent
        if (fieldName === 'account_name' || fieldName === 'network') {
          continue;
        } else if (!value) {
          this.errors[fieldName] = true;
          isValid = false;
        }
      }
      return isValid;
    },
    resetForm() {
      // Values that are always there, regardless of the required fields
      this.form = {
        account_number: { val: '' },
        bank: {
          val: '',
        },
      };
    },
    resetErrors() {
      for (const error in this.errors) {
        this.errors[error] = false;
      }
    },
    attachBankInputListener() {
      const bankListInput = document.getElementById('bank-list-input');
      if (!bankListInput) {
        return;
      }
      const input = bankListInput.getElementsByTagName('input')[0];
      input.addEventListener('keyup', debounce(() => { this.updateBankList(input.value); }, 500));
    },
    updateBankList(value) {
      this.$emit('searchBankParam', value);
    },
    preselectBank() {
      if (this.bankList) {
        this.form.bank.val = this.bankList[0];
        this.handleBankSelect();
      }
      
    },
    preselectRoutingCode() {
      if (this.form.routing_type && this.form.routing_type.acceptedValues.length === 1) {
        this.$set(this.form.routing_type, 'val', this.form.routing_type.acceptedValues[0]);
      }
    },
    preselectAccountType() {
      if (this.form.account_type && this.form.account_type.acceptedValues.length === 1) {
        this.$set(this.form.account_type, 'val', this.form.account_type.acceptedValues[0]);
      }
    },
    buildRoutingCodeLabel() {
      if (this.form.routing_type.val && this.form.routing_type.val.display) {
        return `${this.form.routing_type.val.display}`;
      }
      return 'Routing Code';
    },
    buildAccountNumberLabel() {
      if (this.form.account_type.val && this.form.account_type.val.display) {
        return `${this.form.account_type.val.display}`;
      }
      return 'Account Number';
    },
    cleanAccNum() {
      this.form.account_number.val = this.form.account_number.val.replace(/[^0-9A-Za-z]/g, '');
    },
  },
  watch: {
    requiredFields: {
      handler() {
        this.parseRequiredFields();
      },
      deep: true,
    },
    hasBeenSubmitted(newVal) {
      if (newVal === true) {
        this.handleSubmit();
      }
    },
    bankList: {
      handler(newVal) {
        if (newVal && newVal.length === 0) {
          this.$emit('corridorNotOpen');
        } else {
          this.$emit('corridorOpen');
        }
        this.parseRequiredFields();
      },
      deep: true,

    },
    isBankListLoading(newVal) {
      const ID = '#bank-list-loading';
      if (newVal && document.getElementById(ID)) {
        this.$vs.loading({
          container: ID,
          scale: 0.4,
          background: 'transparent'
        });
      }
      else {
        this.$vs.loading.close(ID + ' > .con-vs-loading');
      }
    },
    accountTypeGetter(newVal) {
      if (newVal) {
        this.dynamicLabels.account_number = this.buildAccountNumberLabel();
      } else {
        this.dynamicLabels.account_number = 'Account Number';
      }
    },
    routingTypeGetter(newVal) {
      if (newVal) {
        this.dynamicLabels.routing_code = this.buildRoutingCodeLabel();
      } else {
        this.dynamicLabels.routing_code = 'Routing Code';
      }
    },
  },
  computed: {
    shouldBankBeHidden() {
      try {
        const isBankListTooShort = this.bankList && this.bankList.length <= 1;
        const shouldBankValueBeHidden = this.form.bank.val && this.form.bank.val.name.toLowerCase() === 'all banks' || this.form.bank.val.name.toLowerCase() === 'sepa banks';
        return isBankListTooShort && shouldBankValueBeHidden;
      } catch (e) {
        return true;
      }
    },
    isCorridorNotOpen() {
      return !this.isBankListLoading && this.bankList && this.bankList.length === 0;
    },
    accountTypeGetter() {
      if (!this.form.account_type || !this.form.account_type.val) {
        return '';
      }
      return this.form.account_type.val.val;
    },
    routingTypeGetter() {
      if (!this.form.routing_type || !this.form.routing_type.val) {
        return '';
      }
      return this.form.routing_type.val.val;
    },
  }
};
</script>

<style lang="scss">
section.destination-account-section {
  form {
    max-width: 700px;

    fieldset {
      border: none;
      margin: $margin-xl 0;
      display: flex;
      flex-wrap: wrap;
      column-gap: 12px;
      row-gap: 6px;
      align-content: flex-start;

      legend {
        font-weight: bold;
        color: $color-primary-light;
      }

      input {
        text-transform: capitalize;
      }

      label {
        position: relative;
        width: calc(50% - 6px);
        min-width: 200px;

        &>* {
          width: 100%;
        }

        &.small {
          width: 250px;
        }

        &.full {
          width: 100%;
        }

        &.three-quarter {
          width: 75%;
        }

        &>span.req {
          color: red;
          width: 1em;
          margin-left: 3px;
        }

        .tooltip {
          right: 0;
          border-radius: 5px;
          margin-left: 3px;

          img {
            height: 16px;
            width: 16px;
            position: relative;
            top: 3px;
          }

          .tooltip-text {
            z-index: 1;
            visibility: hidden;
            position: absolute;
            background: white;
            padding: 10px;
            border-radius: 3px;
            font-size: 12px;
            max-width: 550px;
            min-width: 150px;
            white-space: normal;
            font-weight: normal;
            top: -16px;
            color: $color-text;
            box-shadow: rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 2px 6px 2px;
          }

          &:hover {
            .tooltip-text {
              visibility: visible;
            }
          }
        }

        input {
          padding: 9.8px;
          box-sizing: border-box;
          margin: 0px;
          border: 1px solid #ccc;
          border-radius: 4px;
          border-color: rgba(0, 0, 0, 0.2);
          font-size: 14px;
          color: $color-text;

          &+span {
            display: none;
          }

          &[is-error] {
            border: 1px solid rgb(183, 58, 74);

            &+span {
              color: rgb(183, 58, 74);
              display: inline-block;
              font-size: 9.1px;
              line-height: 13.65px;
            }
          }
        }
      }
    }

    p.banner {
      margin-top: $margin-s;
    }
  }

  h3 {
    color: $color-primary-light;
    font-weight: bold;
    font-size: 18px;
  }

  dl {
    margin-bottom: $margin-l;
    display: grid;
    grid-template-columns: 200px 300px;
    grid-template-rows: repeat(60px);
    justify-content: start;
    justify-items: start;

    div {
      dt {
        color: $color-text-light;
      }

      dd {
        font-weight: bold;
      }

      &.full-row {
        grid-column: 1 / -1;
      }
    }
  }
}
</style>
